// __TELEMATICS__ (2026-10-17) Henry: Azuga ELD on SOME trucks. Trucks > Edit > ELD / telematics: which provider and
// which of its vehicles this truck is, and two switches - "Positions" (the ELD's GPS feeds the live map, arrivals and
// ETAs when it is fresher than the driver's phone) and "Readings" (odometer, engine hours and faults shown here).
// Stored in Settings > Equipment rules (equipment_rules 'main' data.telematics_units) like the operating limits; the hub
// (telematics.py) writes what the ELD reports into data.telematics_readings, shown read-only below. A truck with no
// provider keeps using the driver app's GPS only.
import React, { useEffect, useState } from 'react';
import { fetchRules, updateRules } from '../../services/equipmentService';
import { cleanTelematicsUnits } from '../../utils/equipmentCompat';

const inp = { padding: '0.35rem 0.45rem', backgroundColor: 'var(--surface-input, var(--bg-primary))', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', fontSize: '0.8rem' };
const btn = { padding: '0.35rem 0.7rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem' };
const muted = { fontSize: '0.74rem', color: 'var(--text-muted)' };
export const TELEMATICS_PROVIDERS = { azuga: 'Azuga' };

// one truck's mapping changed in the rules (an empty provider removes it)
export function setTelematicsUnit(rules, truckId, m) {
  const all = { ...((rules && rules.telematics_units) || {}) };
  if (!m || !m.provider) delete all[String(truckId)];
  else all[String(truckId)] = m;
  return { ...rules, telematics_units: cleanTelematicsUnits(all) };
}

const when = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) + ' UTC' : '-');

export function TelematicsEditor({ truckId }) {
  const [rules, setRules] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ provider: '', vehicle_id: '', device_serial: '', positions: false, readings: true });
  useEffect(() => { let on = true; fetchRules(true).then((r) => { if (on) setRules(r); }).catch((e) => on && setErr(String(e.message || e))); return () => { on = false; }; }, []);
  useEffect(() => {
    if (!rules) return;
    const m = (rules.telematics_units || {})[String(truckId)];
    setF(m ? { ...m } : { provider: '', vehicle_id: '', device_serial: '', positions: false, readings: true });
  }, [rules, truckId]);
  if (!truckId) return null;
  const rd = (rules && (rules.telematics_readings || {})[String(truckId)]) || null;
  const save = async () => {
    if (f.provider && !String(f.vehicle_id || '').trim() && !String(f.device_serial || '').trim()) { setErr('Enter the vehicle ID or the device serial from ' + (TELEMATICS_PROVIDERS[f.provider] || f.provider) + '.'); return; }
    setBusy(true); setErr(null);
    try { setRules(await updateRules((r) => setTelematicsUnit(r, truckId, f.provider ? f : null))); } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  return (
    <div data-telematics style={{ gridColumn: '1 / -1', border: '1px solid var(--border-default)', borderRadius: '6px', padding: '0.6rem' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-primary)' }}>ELD / telematics</div>
      <div style={muted}>Only for a truck with an ELD unit. Others keep using the driver app's GPS. Saved with its own button (not with Update).</div>
      {!rules && !err && <div style={muted}>Loading…</div>}
      {rules && (
        <>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.45rem' }}>
            <select value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} style={inp} aria-label="ELD provider">
              <option value="">No ELD</option>
              {Object.keys(TELEMATICS_PROVIDERS).map((k) => <option key={k} value={k}>{TELEMATICS_PROVIDERS[k]}</option>)}
            </select>
            {f.provider && (
              <>
                <input value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} placeholder="Vehicle ID (or its name in Azuga)" style={{ ...inp, minWidth: '220px' }} aria-label="Vehicle ID" />
                <input value={f.device_serial} onChange={(e) => setF({ ...f, device_serial: e.target.value })} placeholder="Device serial" style={{ ...inp, width: '130px' }} aria-label="Device serial" />
              </>
            )}
          </div>
          {f.provider && (
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.4rem', fontSize: '0.8rem' }}>
              <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                <input type="checkbox" checked={!!f.positions} onChange={(e) => setF({ ...f, positions: e.target.checked })} />
                Positions: live map, arrivals and ETAs (when fresher than the phone)
              </label>
              <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                <input type="checkbox" checked={f.readings !== false} onChange={(e) => setF({ ...f, readings: e.target.checked })} />
                Readings: odometer, engine hours, faults
              </label>
            </div>
          )}
          <div style={{ marginTop: '0.4rem' }}>
            <button type="button" disabled={busy} style={btn} onClick={save}>Save ELD settings</button>
          </div>
          {rd && (
            <div data-telematics-readings style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
              <div><strong>From the ELD</strong> <span style={muted}>({TELEMATICS_PROVIDERS[rd.provider] || rd.provider}{rd.vehicle ? ' ' + rd.vehicle : ''})</span></div>
              <div>Odometer: {rd.odometer_km != null ? Math.round(rd.odometer_km).toLocaleString() + ' km' : '-'} · Engine hours: {rd.engine_hours != null ? rd.engine_hours : '-'} <span style={muted}>({when(rd.reading_at)})</span></div>
              <div>Last position: {when(rd.position_at)}{rd.fed === true ? ' - on the map' : rd.fed === false ? ' - not on the map (the phone is fresher, or no driver)' : ''}</div>
              {rd.paired_driver && <div>Logged in on the ELD: {rd.paired_driver}</div>}
              <div>Faults: {(rd.faults || []).length === 0 ? 'none open' : (rd.faults || []).map((x) => x.code + (x.pending ? ' (pending)' : '')).join(', ')}</div>
              {(rd.safety_recent || []).length > 0 && <div style={muted}>Recent: {(rd.safety_recent || []).map((x) => x.event + ' ' + when(x.ts)).join('; ')}</div>}
              <div style={muted}>Hours of service: {rd.hos ? (rd.hos.status || 'shown') : 'not available from this provider yet'} (read-only)</div>
            </div>
          )}
        </>
      )}
      {err && <div style={{ color: 'var(--color-danger, #f85149)', fontSize: '0.78rem', marginTop: '0.3rem' }}>{err}</div>}
    </div>
  );
}
